/**
 * DiscountChip — stage "chip" of the hybrid activation nudge: after the pro
 * dismisses the full-width UnusedDiscountBanner once, this compact header
 * pill becomes the permanent ambient reminder of the unconsumed incentive
 * ("$49 credit" / "50% off" → /pricing). Not dismissible — that's the point
 * of the hybrid: one high-salience banner, then a quiet persistent chip
 * instead of a banner that nags again every session. It disappears for good
 * once the subscription activates (the shared incentive derivation returns
 * null for any non-free plan).
 *
 * Mounted by HeaderNav in the desktop right cluster (lg+) and — via
 * `mobile` — as a row in the mobile nav sheet, since the desktop cluster is
 * `lg:flex`-gated (Pro `lg` = 1280px; same reachability rationale as
 * MobileCopilotOpener). Styled on the header's `pro-ledge` accent; it can
 * never collide with the earned PRO badge, which requires a PAID plan while
 * this chip requires a FREE one.
 *
 * All gating lives in `useDiscountNudge` (shared with the banner): exactly
 * one of banner/chip renders at a time.
 */
import { BadgePercent } from "lucide-react";
import { Link } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import {
  useDiscountNudge,
  useNudgeShownEvent,
} from "../../hooks/useDiscountNudge";

export function DiscountChip({ mobile = false }: { mobile?: boolean }) {
  const nudge = useDiscountNudge();
  const posthog = usePostHog();

  const visible = nudge?.stage === "chip";
  const variant = visible ? nudge.incentive.variant : null;
  useNudgeShownEvent("crm_billing.discount_chip_shown", variant);

  if (!visible) return null;
  const { incentive } = nudge;

  function handleClick() {
    posthog?.capture("crm_billing.discount_chip_clicked", {
      variant: incentive.variant,
    });
  }

  if (mobile) {
    return (
      <Link
        to="/pricing"
        data-testid="discount-chip"
        onClick={handleClick}
        className="flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-semibold text-pro-ledge no-underline outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/30"
      >
        <BadgePercent size={17} strokeWidth={2} aria-hidden />
        <span>Activate Sill Pro</span>
        <span className="ml-auto text-[12px] font-bold">
          {incentive.chipLine}
        </span>
      </Link>
    );
  }

  return (
    <Link
      to="/pricing"
      data-testid="discount-chip"
      aria-label={`Activate Sill Pro — ${incentive.chipLine} waiting`}
      onClick={handleClick}
      className="inline-flex h-[26px] shrink-0 items-center gap-1.5 rounded-full border border-[color-mix(in_oklch,var(--pro-ledge)_45%,transparent)] px-2.5 text-[11.5px] font-bold whitespace-nowrap text-pro-ledge no-underline outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/30"
    >
      <BadgePercent size={13} strokeWidth={2.25} aria-hidden />
      <span>{incentive.chipLine}</span>
    </Link>
  );
}
